import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiClient from '../lib/apiClient';
import { useToast } from '../hooks/use-toast';
import { Button } from '../components/ui/button';
import {
  LayoutDashboard,
  User,
  Code,
  Briefcase,
  GraduationCap,
  BookOpen,
  Lightbulb,
  FileText,
  LogOut,
  Menu,
  X,
} from 'lucide-react';

// Import management components (we'll create these)
import DashboardHome from '../components/admin/DashboardHome';
import PersonalInfoManager from '../components/admin/PersonalInfoManager';
import SkillsManager from '../components/admin/SkillsManager';
import ProjectsManager from '../components/admin/ProjectsManager';
import ExperienceManager from '../components/admin/ExperienceManager';
import BlogManager from '../components/admin/BlogManager';
import PhilosophyManager from '../components/admin/PhilosophyManager';
import CVManager from '../components/admin/CVManager';

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const { toast } = useToast();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const navigationItems = [
    { path: '/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true },
    { path: '/admin/personal-info', label: 'Personal Info', icon: User },
    { path: '/admin/skills', label: 'Skills', icon: Code },
    { path: '/admin/projects', label: 'Projects', icon: Briefcase },
    { path: '/admin/experience', label: 'Experience', icon: GraduationCap },
    { path: '/admin/blog', label: 'Blog', icon: BookOpen },
    { path: '/admin/philosophy', label: 'Philosophy', icon: Lightbulb },
    { path: '/admin/cv', label: 'CV Links', icon: FileText },
  ];

  const handleLogout = async () => {
    await logout();
    toast({
      title: 'Logged out',
      description: 'You have been logged out successfully',
    });
  };

  const isActive = (path, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="flex h-screen">
        {/* Sidebar */}
        <aside
          className={`
            fixed inset-y-0 left-0 z-50 w-64 bg-neutral-900 border-r border-neutral-800
            transform transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0
            ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          `}
        >
          <div className="flex flex-col h-full">
            {/* Sidebar Header */}
            <div className="p-6 border-b border-neutral-800">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-white">Admin Panel</h2>
                  <p className="text-sm text-neutral-400 mt-1">{user?.username || 'Admin'}</p>
                </div>
                <button
                  onClick={() => setIsSidebarOpen(false)}
                  className="lg:hidden text-neutral-400 hover:text-white"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
              {navigationItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path, item.exact);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsSidebarOpen(false)}
                    className={`
                      flex items-center gap-3 px-4 py-3 rounded-lg transition-colors
                      ${
                        active
                          ? 'bg-cyan-500 text-black font-medium'
                          : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
                      }
                    `}
                    data-testid={`admin-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <Icon size={20} />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </nav>

            {/* Logout Button */}
            <div className="p-4 border-t border-neutral-800">
              <Button
                onClick={handleLogout}
                variant="outline"
                className="w-full border-neutral-700 text-neutral-300 hover:bg-neutral-800 hover:text-white"
                data-testid="admin-logout-button"
              >
                <LogOut size={20} className="mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto">
          {/* Mobile Header */}
          <div className="lg:hidden sticky top-0 z-40 bg-neutral-900 border-b border-neutral-800 px-4 py-3">
            <div className="flex items-center justify-between">
              <button
                onClick={() => setIsSidebarOpen(true)}
                className="text-white"
              >
                <Menu size={24} />
              </button>
              <h1 className="text-lg font-semibold">Admin Dashboard</h1>
              <div className="w-6" /> {/* Spacer for centering */}
            </div>
          </div>

          {/* Content Area */}
          <div className="p-6 lg:p-8">
            <Routes>
              <Route path="/" element={<DashboardHome />} />
              <Route path="/personal-info" element={<PersonalInfoManager />} />
              <Route path="/skills" element={<SkillsManager />} />
              <Route path="/projects" element={<ProjectsManager />} />
              <Route path="/experience" element={<ExperienceManager />} />
              <Route path="/blog" element={<BlogManager />} />
              <Route path="/philosophy" element={<PhilosophyManager />} />
              <Route path="/cv" element={<CVManager />} />
              <Route path="*" element={<Navigate to="/admin" replace />} />
            </Routes>
          </div>
        </main>
      </div>

      {/* Sidebar Overlay for Mobile */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
